import { CSSProperties, useEffect, useState } from "react";
import { deleteStageService, getAllStagesService, updateStageService } from "../../../../Services/CRMAdminServices/CRMStagesService";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { Link } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";

/* Where a stage sits, read as a number. Order is stored as text and old rows hold
   blanks and decimals, so anything unreadable goes to the end rather than the top. */
export const orderOf = (s: any): number => {
    const n = parseFloat(String(s?.orderBy ?? ""));
    return isNaN(n) ? 9999 : n;
};

/* Take one tile out of the list and put it back at another place. */
export const moveItem = (list: any[], from: number, to: number): any[] => {
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
    const next = [...list];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    return next;
};

/* Richard, 7 Oct 2026: "I need to have my stage numbers automated so I dont have to
   change them all if I change 1. So cant I have like tiles and I can move up and down."

   So nobody types an order any more. The place a tile sits IS its order: 1 for the top
   tile, 2 for the next, and so on. Move one tile and every stage whose place changed is
   renumbered and saved in the same step. The pipelines and the stage dropdowns read the
   same order, so they follow without anything else being touched. */
export const renumber = (list: any[]): { stage: any; order: string }[] =>
    list.map((stage, i) => ({ stage, order: String(i + 1) }))
        .filter(x => String(x.stage?.orderBy ?? "") !== x.order);

const CRMStages = () => {
    const [stages, setStages] = useState<any[]>([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
    const [dragAt, setDragAt] = useState<number | null>(null);
    const [overAt, setOverAt] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const queryClient = useQueryClient();
    const navigate = useSafeNavigate();

    const { data: stagesData, isLoading, isError } = useGetRequestQuery<any>(
        ['getAllStages'],
        getAllStagesService,
        { enabled: true }
    );

    useEffect(() => {
        if (stagesData && !isError && !isLoading) {
            const sortedStages = [...(stagesData?.data?.data || [])]
                .sort((a, b) => orderOf(a) - orderOf(b) || a.stageId - b.stageId);
            setStages(sortedStages);
        }
    }, [stagesData, isError, isLoading]);

    const { mutate: deleteStage, isPending: isDeleting } = useMutationRequestQuery(
        "deleteStage",
        deleteStageService,
        {
            onSuccess: () => {
                toast.success("Stage deleted successfully.");
                queryClient.invalidateQueries({ queryKey: ["getAllStages"] });
                setDeleteModal(false);
            },
            onError: (e: any) => {
                toast.error(e?.response?.data?.message || "Failed to delete stage.");
                setDeleteModal(false);
            },
        }
    );

    const confirmDelete = (id: number) => {
        setSelectedStageId(id);
        setDeleteModal(true);
    };

    const handleDeleteClick = () => {
        if (selectedStageId) {
            deleteStage(selectedStageId);
        }
    };

    /* Put the tile in its new place straight away, then save every order that changed.
       If the save fails the list goes back to how it was, so the screen never shows an
       order the CRM does not hold. */
    const place = async (from: number, to: number) => {
        if (saving) return;
        const before = stages;
        const next = moveItem(stages, from, to);
        if (next === stages) return;
        const changed = renumber(next);
        setStages(next.map((s, i) => ({ ...s, orderBy: String(i + 1) })));
        if (!changed.length) return;
        setSaving(true);
        try {
            await Promise.all(changed.map(x => updateStageService({
                stageID: x.stage.stageId,
                data: { stageID: x.stage.stageId, name: x.stage.name, orderBy: x.order }
            })));
            queryClient.invalidateQueries({ queryKey: ["getAllStages"] });
        } catch {
            setStages(before);
            toast.error("Order not saved. Try again.");
        }
        setSaving(false);
    };

    const arrow = (disabled: boolean): CSSProperties => ({
        border: '1px solid #d5d9e6', background: '#fff', borderRadius: '4px', width: '26px', height: '24px',
        lineHeight: '20px', padding: 0, fontSize: '11px', color: disabled ? '#c5c9d6' : '#0033cc',
        cursor: disabled ? 'default' : 'pointer'
    });

    return (
        <div className="px-4 pt-4" style={{ minHeight: '500px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <div style={{ borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', padding: '10px', backgroundColor: '#fff' }}>
                <div className="g-3 mb-2 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-baseline" style={{ gap: '12px' }}>
                        <h3 className="f-w-900 heading-color mx-3 py-2 mb-0">Stages List</h3>
                        <span style={{ color: '#767d95', fontSize: '13px' }}>
                            {saving ? 'Saving order…' : 'Drag a tile up or down, or use the arrows. The order saves itself.'}
                        </span>
                    </div>
                    <Button color="primary" className="mx-3" onClick={() => navigate("/AddStages")}>Add Stage</Button>
                </div>
                {isLoading ? (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                        }}
                        className='mt-4 mb-2'
                    >
                        <Spinner color="primary" />
                    </div>
                ) : stages.length !== 0 ? (
                    <div className="mx-3 mb-3" style={{ maxWidth: '640px' }}>
                        {stages.map((row: any, i: number) => (
                            <div
                                key={row.stageId}
                                draggable={!saving}
                                onDragStart={() => setDragAt(i)}
                                onDragEnd={() => { setDragAt(null); setOverAt(null); }}
                                onDragOver={e => { e.preventDefault(); if (overAt !== i) setOverAt(i); }}
                                onDrop={e => {
                                    e.preventDefault();
                                    const from = dragAt;
                                    setDragAt(null); setOverAt(null);
                                    if (from !== null) place(from, i);
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '12px',
                                    background: '#fff', borderRadius: '6px', padding: '8px 12px', marginBottom: '6px',
                                    border: overAt === i && dragAt !== null && dragAt !== i ? '2px solid #0033cc' : '1px solid #e6e8f0',
                                    cursor: saving ? 'default' : 'grab', opacity: dragAt === i ? 0.45 : 1
                                }}
                            >
                                <span style={{ color: '#9aa1b5', fontSize: '16px', lineHeight: 1 }} title="Drag to move">&#8942;&#8942;</span>
                                <span style={{ width: '28px', textAlign: 'right', color: '#767d95', fontSize: '13px' }}>{i + 1}</span>
                                <span style={{ flex: 1, fontWeight: 700, fontSize: '14px', color: '#111528' }}>{row.name}</span>
                                {/*  The stage number is the value stored on every lead. It never
                                     changes when a tile is moved - only the place in the list does. */}
                                <span style={{ color: '#9aa1b5', fontSize: '12px', whiteSpace: 'nowrap' }}>Stage Number {row.stageId}</span>
                                <button type="button" title="Move up" disabled={saving || i === 0}
                                    onClick={() => place(i, i - 1)} style={arrow(saving || i === 0)}>&#9650;</button>
                                <button type="button" title="Move down" disabled={saving || i === stages.length - 1}
                                    onClick={() => place(i, i + 1)} style={arrow(saving || i === stages.length - 1)}>&#9660;</button>
                                <ul className="action simple-list flex-row mb-0">
                                    <li className="edit">
                                        <Link to={`/EditStages/${row.stageId}`}>
                                            <i className="icon-pencil-alt" />
                                        </Link>
                                    </li>
                                </ul>
                                {/*  Richard, 15 Oct 2026: "add delete buttons". The trash icon was there but
                                     did not show. A stage that still has leads on it cannot be deleted - the
                                     API says how many, so they can be moved first. */}
                                <button type="button" title="Delete stage" disabled={saving}
                                    onClick={() => confirmDelete(row.stageId)}
                                    style={{ border: '1px solid #f2c4c4', background: '#fff5f5', color: '#b3261e', borderRadius: 6,
                                             padding: '4px 10px', fontSize: 12.5, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                        }}
                        className='mt-4 mb-2'
                    >
                        <h6>No Stages data available.</h6>
                    </div>
                )}

            </div>
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Confirm Delete</ModalHeader>
                <ModalBody>Delete {stages.find(x => x.stageId === selectedStageId)?.name || "this stage"}? A stage with leads on it cannot be deleted.</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setDeleteModal(false)}>Cancel</Button>
                    <Button color="danger" onClick={handleDeleteClick} disabled={isDeleting}>
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CRMStages;
